import React, { useState, useEffect } from "react";
import { LikeIcon, LikedIcon, ReplyIcon, EditPencilComment, DeleteBinComment } from "../../assets/icons";
import profile from "../../assets/icons/Avatar Goes Here.png";
import CommentBox from "./CommentBox";
import { getRelativeTime } from "../../utils/dateUtils";
import { editProgressComment, deleteProgressComment, likeComment } from "../../services/service";
import { useSelector } from 'react-redux';
import { selectCurrentPlan } from '../../redux/selectors/authSelectors';
import { FEATURE_REQUIRED_PLANS } from '../../utils/planFeatureAccess';
import UpgradePromptModal from './UpgradePromptModal';
import { toast } from 'react-toastify';
import { IMAGE_BASE_URL } from "../../config/api";


const CommentsSection = ({ 
  comments = [], 
  onReply, 
  onUpdate,
  editComment = editProgressComment,
  deleteComment = deleteProgressComment,
  commentType = 'wp_comment'
}) => {
  const [replyingTo, setReplyingTo] = useState(null);
  const [editingComment, setEditingComment] = useState(null);
  const [editText, setEditText] = useState('');
  const [showUpgradeModal, setShowUpgradeModal] = useState(false);
  const currentPlan = useSelector(selectCurrentPlan);
  const [editError, setEditError] = useState('');

  const canAccessFeature = (feature) => {
    const planLevels = {
      'trial': 0,
      'basic': 1,
      'premium': 2,
      'ultimate': 3
    };
 
    
    return planLevels[currentPlan] >= planLevels[FEATURE_REQUIRED_PLANS[feature]];
  };

  const handleAction = (action, ...args) => {
    if (!canAccessFeature('commenting')) {
      setShowUpgradeModal(true);
      return;
    }
    action(...args);
  };

  const handleReplyClick = (commentId) => {
    setReplyingTo(replyingTo === commentId ? null : commentId);
    setEditingComment(null);
  };

  const handleEditClick = (comment) => {
    setEditError('');
    
    if (editingComment?.id === comment.id) {
      setEditingComment(null);
      setEditText('');
    } else {
      setEditingComment(comment);
      setEditText(comment.comment);
      setReplyingTo(null);
    }
  };

  const handleEditSubmit = async (commentId) => {
    try {
      const response = await editComment({
        commentId: commentId,
        comment: editText
      });
      
      if (response.success) {
        setEditingComment(null);
        setEditText('');
        if (onUpdate) onUpdate();
      } else {
        toast.error(response.message || 'You can only edit comments within 5 minutes of creation.');
        setEditingComment(null);
        setEditText('');
      }
    } catch (error) {
      console.error("Error editing comment:", error);
      toast.error('You can only edit comments within 5 minutes of creation.');
      setEditingComment(null);
      setEditText('');
    }
  };

  const handleDeleteComment = async (commentId) => {
    if (true) {
      try {
        const response = await deleteComment(commentId);
        
        if (response.success) {
          if (onUpdate) onUpdate();
        }
      } catch (error) {
        console.error("Error deleting comment:", error);
      }
    }
  };

  const handleLike = async (commentId) => {
    if (!canAccessFeature('commenting')) {
      setShowUpgradeModal(true);
      return;
    }

    try {
      const response = await likeComment({
        id: commentId,
        type: commentType
      });
      
      if (response.success) {
        if (onUpdate) onUpdate();
      }
    } catch (error) {
      console.error("Error liking comment:", error);
    }
  };

  const isCommentLiked = (comment) => {
    if (commentType === 'wp_comment') {
      return comment.progressLike && comment.progressLike.length > 0;
    } else if (commentType === 'attachment_comment') {
      return comment.progressAttachmentLike && comment.progressAttachmentLike.length > 0;
    }
    return false;
  };

  const renderCommentContent = (comment) => {
    if (editingComment?.id === comment.id) {
      return (
        <div className="mt-2">
          <textarea
            value={editText}
            onChange={(e) => setEditText(e.target.value)}
            className="w-full p-2 border border-gray-300 rounded-md focus:outline-none focus:border-orange-500"
            rows={3}
          />
          {editError && (
            <p className="text-red-500 text-sm mt-1">{editError}</p>
          )}
          <div className="flex gap-2 mt-2">
            <button
              onClick={() => handleEditSubmit(comment.id)}
              className="px-3 py-1 bg-orange-500 text-white rounded-md text-sm hover:bg-orange-600"
            >
              Save
            </button>
            <button
              onClick={() => {
                setEditingComment(null);
                setEditText('');
                setEditError('');
              }}
              className="px-3 py-1 bg-gray-200 text-gray-700 rounded-md text-sm hover:bg-gray-300"
            >
              Cancel
            </button>
          </div>
        </div>
      );
    }
    return <p className="text-sm mt-1">{comment.comment}</p>;
  };

  const renderCommentActions = (comment) => (
    <div className={`flex items-center gap-4 mt-2 ${!canAccessFeature('commenting') ? 'opacity-50' : ''}`}>
      <div 
        className="flex gap-1 items-center cursor-pointer"
        onClick={() => handleAction(() => handleLike(comment.id))}
      >
        <span>{isCommentLiked(comment) ? LikedIcon : LikeIcon}</span>
        <p className={`font-medium text-[13px] ${isCommentLiked(comment) ? 'text-[#4B5563]' : 'text-[#B7B7B7]'}`}>
          Like
        </p>
      </div>
      <div 
        className="flex gap-1 items-center cursor-pointer"
        onClick={() => handleAction(() => handleReplyClick(comment.id))}
      >
        <span>{ReplyIcon}</span>
        <p className="font-medium text-[13px] text-[#B7B7B7]">
          {replyingTo === comment.id ? 'Cancel' : 'Reply'}
        </p>
      </div>
      
      {editingComment?.id !== comment.id && (
        <div 
          className="flex gap-1 items-center cursor-pointer"
          onClick={() => handleAction(() => handleEditClick(comment))}
        >
          <span>{EditPencilComment}</span>
          <p className="font-medium text-[13px] text-[#B7B7B7]">Edit</p>
        </div>
      )}
      
      <div 
        className="flex gap-1 items-center cursor-pointer"
        onClick={() => handleAction(() => handleDeleteComment(comment.id))}
      >
        <span>{DeleteBinComment}</span>
        <p className="font-medium text-[13px] text-red-500">Delete</p>
      </div>
    </div>
  );

  return (
    <div className="w-full">
      <h3 className="font-medium text-base text-[#4B5563] mb-3">Comments</h3>
      <div className="space-y-4">
        {comments.map((comment) => (
          <div key={comment.id} className="">
            <div className="flex items-center gap-2">
              <div className="w-[24px] h-[24px] flex justify-center items-center rouded-full">
                <img
                  src={
                    comment.User?.image
                      ? (comment.User.image.startsWith('http')
                          ? comment.User.image
                          : `${IMAGE_BASE_URL}${comment.User.image}`)
                      : profile
                  }
                  alt="profile"
                  className="w-full h-full rounded-full object-cover"
                />
              </div>
              <span className="font-semibold text-[14px] text-[#4B5563]">
                {comment.User?.username || "Anonymous"}
              </span>
              <span className="font-normal text-[14px] text-[#B7B7B7]">
                {getRelativeTime(comment.createdAt)}
              </span>
            </div>
            
            {renderCommentContent(comment)}
            {renderCommentActions(comment)}

            {replyingTo === comment.id && (
              <div className="ml-8 mt-2">
                <CommentBox
                  worker={true}
                  placeholder="Write a reply..."
                  onSubmit={(replyText) => {
                    onReply(replyText, comment.id);
                    setReplyingTo(null);
                  }}
                  autoFocus={true}
                />
              </div>
            )}

            {/* Show replies */}
            {comment.Replies && comment.Replies.length > 0 && (
              <div className="ml-6 mt-3 space-y-2">
                {comment.Replies.map((reply) => (
                  <div key={reply.id} className="rounded-md p-2">
                    <div className="flex items-center gap-2">
                      <div className="w-[24px] h-[24px] flex justify-center items-center rouded-full">
                        <img
                          src={
                            reply.User?.image
                              ? (reply.User.image.startsWith('http')
                                  ? reply.User.image
                                  : `${IMAGE_BASE_URL}${reply.User.image}`)
                              : profile
                          }
                          alt="profile"
                          className="w-full h-full rounded-full object-cover"
                        />
                      </div>
                      <span className="font-semibold text-[14px] text-[#4B5563]">
                        {reply.User?.username || "Anonymous"}
                      </span>
                      <span className="font-normal text-[14px] text-[#B7B7B7]">
                        {getRelativeTime(reply.createdAt)}
                      </span>
                    </div>
                    {renderCommentContent(reply)}
                    {renderCommentActions(reply)}
                  </div>
                ))}
              </div>
            )}
          </div>
        ))}
      </div>

      <UpgradePromptModal
        show={showUpgradeModal}
        handleClose={() => setShowUpgradeModal(false)}
        featureName="Commenting"
        requiredPlan="Premium"
      />
    </div>
  );
};

export default CommentsSection;
